import { setPageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getService } from "@/data/services";
import { EnquiryModal } from "@/components/wve/EnquiryModal";
import { PageCTA } from "@/components/wve/PageCTA";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const VideoCard = ({ id, title, index }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 2) * 0.08 }}
      className="group"
      data-testid={`showreel-video-${index + 1}`}
    >
      <div className="relative aspect-video rounded-lg overflow-hidden bg-[#0A0A0A]">
        {playing ? (
          <iframe
            src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`}
            title={title}
            className="w-full h-full border-0"
            allow="autoplay; fullscreen; encrypted-media"
          />
        ) : (
          <button
            onClick={() => setPlaying(true)}
            data-testid={`showreel-video-play-${index + 1}`}
            className="w-full h-full block"
          >
            <img
              src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`}
              alt={title}
              className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-[opacity,transform] duration-500"
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full bg-[#E80042] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <span className="w-0 h-0 border-y-[10px] border-y-transparent border-l-[16px] border-l-white ml-1 inline-block" />
              </span>
            </span>
          </button>
        )}
      </div>
      <p className="font-grotesk font-bold mt-3">{title}</p>
    </motion.div>
  );
};

export default function ServicePage() {
  const { slug } = useParams();
  const s = getService(slug);
  const [enquireOpen, setEnquireOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (s) {
      setPageMeta(
        `${s.title} in Essex | WVE Studios`,
        `${s.tag} ${s.paragraphs[0]}`.slice(0, 158),
        `/services/${s.slug}`
      );
      if (s.faqs) {
        let ld = document.getElementById("faq-jsonld");
        if (!ld) {
          ld = document.createElement("script");
          ld.type = "application/ld+json";
          ld.id = "faq-jsonld";
          document.head.appendChild(ld);
        }
        const prices = { "artist-showreels": "399", "dance-shows": "22" };
        ld.textContent = JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "FAQPage",
              mainEntity: s.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "Service",
              name: s.title,
              description: s.tag,
              provider: { "@id": "https://wvestudios.co.uk/#org" },
              areaServed: "Essex, United Kingdom",
              url: `https://wvestudios.co.uk/services/${s.slug}`,
              ...(prices[s.slug] ? { offers: { "@type": "Offer", price: prices[s.slug], priceCurrency: "GBP" } } : {}),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://wvestudios.co.uk/" },
                { "@type": "ListItem", position: 2, name: "Services", item: "https://wvestudios.co.uk/services" },
                { "@type": "ListItem", position: 3, name: s.title, item: `https://wvestudios.co.uk/services/${s.slug}` },
              ],
            },
          ],
        });
      }
    }
    return () => document.getElementById("faq-jsonld")?.remove();
  }, [slug, s]);

  if (!s) return <Navigate to="/" replace />;

  return (
    <main data-testid={`service-page-${s.slug}`}>
      <section className="bg-[#0A0A0A] text-white pt-32 sm:pt-40 pb-16 relative overflow-hidden">
        <span className="absolute -right-10 -top-6 font-display text-[24rem] leading-none text-white/[0.05] select-none hidden lg:block">{s.n}</span>
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 relative">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Link to="/#services" data-testid="service-back-link" className="text-sm uppercase tracking-[0.25em] text-white/50 hover:text-[#E80042] transition-colors duration-200">
              ← All Services
            </Link>
            <p className="text-sm uppercase tracking-[0.3em] font-semibold text-[#E80042] mt-8 mb-4">Service {s.n}</p>
            <h1 className="font-display uppercase leading-[0.95] text-5xl sm:text-7xl lg:text-8xl tracking-tight max-w-5xl">
              {s.title}
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed">{s.tag}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-4 lg:sticky lg:top-28 self-start space-y-6"
          >
            <div className="rounded-lg overflow-hidden -rotate-1">
              <img src={s.heroImg} alt={s.title} className="w-full h-[380px] object-cover" />
            </div>
            {s.price && (
              <div className="bg-[#E80042] text-white rounded-lg p-6" data-testid="service-price-card">
                <p className="font-display text-4xl uppercase">{s.price}</p>
                <p className="text-white/90 text-sm mt-2 leading-relaxed">{s.priceNote}</p>
              </div>
            )}
            {s.stat && (
              <div className="bg-[#0A0A0A] text-white rounded-lg p-6" data-testid="service-stat-card">
                <p className="font-display text-5xl text-[#E80042]">{s.stat.big}</p>
                <p className="text-white/70 text-sm mt-2 leading-relaxed">{s.stat.label}</p>
              </div>
            )}
            <div className="bg-white border border-black/10 rounded-lg p-6">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#555555] mb-4">What's included</p>
              <ul className="space-y-3">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[#0A0A0A]">
                    <span className="w-2 h-2 rounded-full bg-[#E80042] mt-2 shrink-0" />
                    <span className="font-medium">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <button
              onClick={() => setEnquireOpen(true)}
              data-testid="service-cta-button"
              className="block w-full text-center bg-[#0A0A0A] text-white font-grotesk font-bold text-lg px-9 py-4 rounded-full hover:bg-[#E80042] transition-colors duration-300 active:scale-95"
            >
              Enquire Now →
            </button>
            <a
              href="tel:01277280512"
              data-testid="service-phone-button"
              className="block text-center border-2 border-[#0A0A0A] text-[#0A0A0A] font-grotesk font-bold text-lg px-9 py-4 rounded-full hover:bg-[#0A0A0A] hover:text-white transition-colors duration-300"
            >
              Call 01277 280 512
            </a>
            {s.external && (
              <a
                href={s.external.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-external-link"
                className="block text-center border-2 border-[#E80042] text-[#E80042] font-grotesk font-bold text-lg px-9 py-4 rounded-full hover:bg-[#E80042] hover:text-white transition-colors duration-300"
              >
                {s.external.label} ↗
              </a>
            )}
          </motion.aside>

          <div className="lg:col-span-8 space-y-12">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }} className="space-y-6">
              {s.paragraphs.map((p, i) => (
                <p key={i} className={`leading-relaxed ${i === 0 ? "font-grotesk font-bold text-2xl sm:text-3xl text-[#0A0A0A]" : "text-lg text-[#555555]"}`}>
                  {p}
                </p>
              ))}
            </motion.div>

            {s.video && (
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7 }}
                data-testid="service-video-embed"
              >
                <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E80042] mb-4">▶ {s.videoLabel}</p>
                <div className="aspect-video rounded-lg overflow-hidden bg-[#0A0A0A]">
                  <iframe
                    src={`https://www.youtube.com/embed/${s.video}?rel=0`}
                    title={s.videoLabel}
                    className="w-full h-full border-0"
                    allow="autoplay; fullscreen; encrypted-media"
                  />
                </div>
              </motion.div>
            )}

            {s.videos?.length > 0 && (
              <div data-testid="showreel-videos-grid">
                <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E80042] mb-6">▶ Watch our artist showreels</p>
                <div className="grid sm:grid-cols-2 gap-6">
                  {s.videos.map((v, i) => (
                    <VideoCard key={v.id} id={v.id} title={v.title} index={i} />
                  ))}
                </div>
              </div>
            )}

            {s.gallery?.length > 0 && (
              <div data-testid="service-gallery-section">
                {s.galleryTitle && (
                  <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E80042] mb-6">{s.galleryTitle}</p>
                )}
                <div className={`grid gap-5 ${s.gallery.length > 2 ? "grid-cols-2 sm:grid-cols-3" : "sm:grid-cols-2"}`}>
                {s.gallery.map((g, i) => (
                  <motion.img
                    key={g}
                    src={g}
                    alt={`${s.title} ${i + 1}`}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                    className={`w-full object-cover object-[center_20%] rounded-lg ${s.gallery.length === 1 ? "sm:col-span-2 h-[320px]" : "h-[260px] sm:h-[320px]"}`}
                  />
                ))}
                </div>
              </div>
            )}

            {s.bts && (
              <div data-testid="service-bts-section">
                <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight mb-4">
                  <span className="text-[#E80042] mr-3">●</span>{s.bts.title}
                </h2>
                <p className="text-lg text-[#555555] leading-relaxed mb-8 max-w-3xl">{s.bts.intro}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                  {s.bts.images.map((img, i) => (
                    <motion.img
                      key={img.src}
                      src={img.src}
                      alt={img.alt}
                      loading="lazy"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                      data-testid={`service-bts-image-${i + 1}`}
                      className={`w-full object-cover rounded-lg h-[260px] sm:h-[340px] ${img.wide ? "col-span-2" : ""}`}
                    />
                  ))}
                </div>
              </div>
            )}
            {s.faqs?.length > 0 && (
              <div data-testid="service-faq-section">
                <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight mb-6">
                  <span className="text-[#E80042] mr-3">?</span>Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="w-full">
                  {s.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} data-testid={`service-faq-${i + 1}`}>
                      <AccordionTrigger className="text-left font-grotesk font-bold text-lg hover:text-[#E80042] hover:no-underline">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-[#555555] text-base leading-relaxed">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>
        </div>
      </section>

      <PageCTA context={s.title} title="Ready to roll?" subtitle={s.tag} />
      <EnquiryModal open={enquireOpen} onClose={() => setEnquireOpen(false)} context={s.title} />
    </main>
  );
}
